import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { Box, Button, DropdownMenu, Flex, Select, Spinner, Table, TextField } from '@radix-ui/themes';
import { debounce, isEmpty } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { SEARCH_BY_BUTTON, SEARCH_BY_ENTER } from '../../utils/constants';
import CheckboxComponent from '../inputs/Checkbox/CheckboxComponent';
import { useExportToExcel } from '../../hooks/useExportToExcel';

interface Option {
  label: string;
  onClick: (item?: any) => void;
  icon?: any;
  component?: any;
}

interface ITableComponent {
  columns?: string[];
  items?: Array<{ [key: string]: any }>;
  options?: Option[][];
  columnNamesMap: { [key: string]: string };
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  filterFields?: any;
  totalColumns?: string[];
  onSearch?: (searchTerm: string) => void;
  search?: string;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  isFetching?: boolean;
  totalItems?: number;
  enableSearchByEnter?: boolean;
  enableSearchByBtn?: boolean;
  enableSearch?: boolean;
  enablePaginate?: boolean;
  control?: any;
  errors?: any;
  setValue?: any;
  getValues?: any;
  optionsForMassiveItems?: Option[];
  footerContent?: React.ReactNode;
     enableExportToExcel?: boolean; 
  exportFileName?: string;
}

const TableComponent = ({
  columns = [],
  items = [],
  options = [],
  columnNamesMap,
  totalPages,
  currentPage,
  onPageChange,
  filterFields,
  totalColumns = [],
  onSearch,
  search = '',
  pageSize = 5,
  onPageSizeChange,
  isFetching = false,
  totalItems,
  enableSearchByBtn = SEARCH_BY_BUTTON,
  enableSearchByEnter = SEARCH_BY_ENTER,
  enableSearch = true,
  enablePaginate = true,
  control,
  errors,
  setValue,
  getValues,
  optionsForMassiveItems = [],
  footerContent = null,
  enableExportToExcel = false, 
  exportFileName = `${new Date().toUTCString()}exporter.xlsx`,
}: ITableComponent) => {
  const [searchTerm, setSearchTerm] = useState(search);
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);
  const { exportToExcel } = useExportToExcel();

  const debouncedSearch = useCallback(
    debounce((term: string) => {
      if (onSearch) {
        onSearch(term);
      }
    }, 900),
    [onSearch]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (enableSearchByEnter) return;
    if (enableSearchByBtn) return;
    if (onSearch) {
      debouncedSearch(value);
    }
  };

  const handlePageSizeChange = (value: string) => {
    const size = parseInt(value, 10);
    setCurrentPageSize(size);
    if (onPageSizeChange) {
      onPageSizeChange(size);
    }
  };

  const filteredItems = onSearch
    ? items
    : searchTerm
      ? items.filter((item) =>
        columns.some((column) =>
          item[column]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
      : items;

  const totals: { [key: string]: number } = {};

  if (totalColumns.length > 0) {
    totalColumns.forEach((column) => {
      totals[column] = filteredItems.reduce((sum, item) => sum + (item[column] || 0), 0);
    });
  }

  const handleSearchByPressingEnter = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (enableSearchByEnter) {
      event.key === 'Enter' && enableSearchByEnter && onSearch && onSearch(searchTerm);
    }
  }

  useEffect(() => {
    if (isEmpty(searchTerm)) {
      onSearch && onSearch(searchTerm);
    }
  }, [searchTerm])

  const handleCheckBoxSelectAll = (checked: boolean) => {
    filteredItems.map((item, index) => {
      setValue(`item.${index}`, checked)
    });
  }

  const handleOnclickBtnMassiveItems = (option: Option) => {
    const selectedItems = filteredItems.filter((item, index) => {
      const itemIsChecked = getValues(`item.${index}`);
      return itemIsChecked;
    });

    option.onClick(selectedItems);
    filteredItems.map((item, index) => {
      setValue(`item.${index}`, false)
    });
    setValue('selectAll', false)
  };

  return (
    <Flex direction='column' gap='4'>
      <Flex justify="between" align="center" gap="4" wrap="wrap">
        <Flex gap="2" align="center">
          {enableSearch && (
            <TextField.Root
              size="2"
              onKeyDown={handleSearchByPressingEnter}
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Pesquisar"
            >

              <TextField.Slot>
                <MagnifyingGlassIcon height="16" width="16" />
              </TextField.Slot>

            </TextField.Root>
          )}
          {enableSearchByBtn && (
            <Button onClick={() => onSearch && onSearch(searchTerm)}>
              <MagnifyingGlassIcon height="16" width="16" />
            </Button>
          )}
  {enableExportToExcel && (
            <Button
              variant="surface"
              onClick={() => {
                const mappedForExcel = filteredItems.map(item => {
                  const newItem: { [key: string]: any } = {};
                  columns.forEach(column => {
                    const header = columnNamesMap[column] || column;
                    newItem[header] = item[column];
                  });
                  return newItem;
                });

                exportToExcel(mappedForExcel, exportFileName);
              }}
            >
              Exportar para Excel
            </Button>
          )}

          {
            control && optionsForMassiveItems.length > 0 && optionsForMassiveItems.map((option, index) => (
              <Button onClick={() => handleOnclickBtnMassiveItems(option)} variant='surface'
              >
                {option.label} {option.icon}
              </Button>
            ))
          }

        </Flex>
        <Flex gap="2" align="center">{filterFields && filterFields()}</Flex>
        {totalItems !== undefined && (<>
          <span>Total: {totalItems}   </span>
        </>
        )}
        {onPageSizeChange && (
          <>

            <Select.Root value={currentPageSize.toString()} onValueChange={handlePageSizeChange}>
              <Select.Trigger />
              <Select.Content>
                <Select.Item value="50">50</Select.Item>
                <Select.Item value="100">100</Select.Item>
                <Select.Item value="300">300</Select.Item>
                <Select.Item value="500">500</Select.Item>
                <Select.Item value="1000">1000</Select.Item>
              </Select.Content>
            </Select.Root>

          </>
        )}

      </Flex>
      {isFetching && (
        <Box style={{ textAlign: 'center', margin: '10px 0' }}>
          <Spinner />
        </Box>
      )}
      <Table.Root variant="surface">
        <Table.Header>
          {totalColumns.length > 0 && (
            <Table.Row>
              {columns.map((column) => (
                <Table.Cell key={column} style={{ fontWeight: 'bold' }}>
                  {totalColumns.includes(column) ? totals[column] : ''}
                </Table.Cell>
              ))}

            </Table.Row>
          )}
          <Table.Row>
            {control &&
              <Table.Cell>
                <CheckboxComponent
                  name='selectAll'
                  label=''
                  control={control || undefined}
                  errors={errors || {}}
                  onChange={handleCheckBoxSelectAll}
                />

              </Table.Cell>

            }
            {columns.map((column) => (
              <Table.ColumnHeaderCell key={column}>
                {columnNamesMap[column] || column}
              </Table.ColumnHeaderCell>
            ))}
            {options.length > 0 && <Table.ColumnHeaderCell style={{ textAlign: 'right' }}>Operações</Table.ColumnHeaderCell>}

          </Table.Row>
        </Table.Header>

        <Table.Body>
          {filteredItems.map((item, index) => (
            <Table.Row key={index}>
              {control &&
                <Table.Cell>
                  <CheckboxComponent
                    name={`item.${index}`}
                    label=''
                    control={control || undefined}
                    errors={errors || {}}
                    onChange={(checked) => {
                      console.log(`Checkbox do item ${index} mudou para:`, checked);
                    }}
                  />

                </Table.Cell>

              }
              {columns.map((column) => (
                <Table.Cell key={column}>{item[column]}</Table.Cell>
              ))}
              {options.length > 0 && (
                <Table.Cell style={{ textAlign: 'right', paddingRight: '30px' }}>
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger>
                      <Button variant="soft">
                        <DropdownMenu.TriggerIcon />
                      </Button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content>
                      {options[index]?.map((option, i) => (
                        <DropdownMenu.Item key={i} onSelect={() => option.onClick(item)}>
                          {option?.icon}
                          {option.label}
                        </DropdownMenu.Item>
                      ))}
                    </DropdownMenu.Content>
                  </DropdownMenu.Root>
                </Table.Cell>
              )}

            </Table.Row>
          ))}

          {totalColumns.length > 0 && (
            <Table.Row>
              {columns.map((column) => (
                <Table.Cell key={column} style={{ fontWeight: 'bold' }}>
                  {totalColumns.includes(column) ? totals[column] : ''}
                </Table.Cell>
              ))}

            </Table.Row>
          )}
        </Table.Body>
      </Table.Root>
      {footerContent && (
        <Box style={{ marginTop: '20px' }}>
          {footerContent}
        </Box>
      )}
      {enablePaginate && (
        <Box style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
          <Button disabled={currentPage <= 1} onClick={() => onPageChange(currentPage - 1)}>
            Anterior
          </Button>
          <Box style={{ margin: '0 10px' }}>Página {currentPage} de {totalPages}

          </Box>
          <Button disabled={currentPage >= totalPages} onClick={() => onPageChange(currentPage + 1)}>
            Próxima
          </Button>
        </Box>
      )}

    </Flex>
  );
};

export default TableComponent;